import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { IHeaderParams } from 'ag-grid-community';
import Cell from '../V5Gadget/Matrix/Cell';
import { Option } from '../../kingpin/atoms/ContextMenu';
import usePopup from '../../hooks/usePopup';
import InlineDialog from '../InlineDialog';
import { MatrixSortContext } from '../V5Gadget/Matrix/MatrixSortContext';
import { SortToggleContext } from '../V5GadgetForm/SortToggleContext';

export const DELTA_HEADER_CELL = 'DELTA_HEADER_CELL';

interface HeaderParams extends IHeaderParams {
  title: string;
  defaultSortField?: string;
}

const HeaderContextMenu = styled.div`
  display: none;
  position: absolute;
  right: 15px;
  top: 0;

  &:focus {
    display: block;
  }
`;

const Wrapper = styled.div<{ rightAlign?: boolean; centerAlign: boolean }>`
  overflow: unset !important;
  text-overflow: clip !important;
  white-space: normal !important;
  cursor: pointer;
  flex: 1;
  height: 100%;

  display: flex;
  flex-direction: row;
  justify-content: ${(props) =>
    props.centerAlign
      ? 'center'
      : props.rightAlign
        ? 'flex-end'
        : 'flex-start'};
  align-items: center;

  &:hover ${HeaderContextMenu} {
    display: block;
  }
`;

const isHeaderParams = (params: HeaderParams): params is HeaderParams => {
  // @ts-ignore
  return params.progressSort !== undefined;
};

const DeltaHeaderCell = (props: HeaderParams) => {
  const { title } = props;
  const { isOpen, open, close } = usePopup();
  const { setIsSortAbsolute, setSortField, sortField, setSortDirection } =
    useContext(MatrixSortContext);
  const { setDefaultSort } = useContext(SortToggleContext);

  const [sort, setSort] = useState<'asc' | 'desc' | undefined | null>(
    isHeaderParams(props) ? props.column.getSort() : undefined,
  );

  const isShowingSortArrow = useMemo(() => {
    if (sortField && sortField !== props.column.getColId()) return false;
    return (
      props.defaultSortField === props.column.getColId() ||
      sortField === props.column.getColId()
    );
  }, [props.column, props.defaultSortField, sortField]);

  const onSort = useCallback(
    (sortDirection: 'asc' | 'desc', isAbsolute: boolean) => {
      if (isHeaderParams(props)) {
        // v35 removed Column.setSort(); drive the column sort state via the api
        // so the row sort + sort arrow update (as they did on v31).
        props.api.applyColumnState({
          state: [{ colId: props.column.getColId(), sort: sortDirection }],
          defaultState: { sort: null },
        });
        props.setSort(sortDirection);
        props.context.isAbsolute = isAbsolute;
        setSort(isHeaderParams(props) ? props.column.getSort() : undefined);
        setDefaultSort({
          field: props.column.getColId(),
          direction: sortDirection,
          isAbsolute: isAbsolute,
          isComparison: true,
          metricId: props.column.getColId().split('-')[0],
        });
        setIsSortAbsolute(isAbsolute);
        setSortField(props.column.getColId());
        setSortDirection(sortDirection);
      }
    },
    [setDefaultSort, props, setIsSortAbsolute, setSortDirection, setSortField],
  );

  const onMenuClicked = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      open();
    },
    [open],
  );

  return (
    <>
      <Cell onClick={onMenuClicked} disableLink>
        <Wrapper centerAlign={!isHeaderParams(props)}>
          <span>{title}</span>
          {isShowingSortArrow && <SortArrow sort={sort} />}
          <InlineDialog
            isOpen={isOpen}
            onClose={close}
            content={
              <>
                <Option
                  label={'Sort asc'}
                  icon={'arrow-up'}
                  onClick={() => onSort('asc', false)}
                />
                <Option
                  label={'Sort desc'}
                  icon={'arrow-down'}
                  onClick={() => onSort('desc', false)}
                />
                <Option
                  label={'Sort asc (Absolute)'}
                  icon={'arrow-up'}
                  onClick={() => onSort('asc', true)}
                />
                <Option
                  label={'Sort desc (Absolute)'}
                  icon={'arrow-down'}
                  onClick={() => onSort('desc', true)}
                />
              </>
            }
          >
            <div></div>
          </InlineDialog>
        </Wrapper>
      </Cell>
    </>
  );
};

const SortArrow = ({ sort }: { sort?: 'asc' | 'desc' | null }) => (
  <>
    {sort && sort === 'asc' && (
      <span
        style={{ marginRight: 4, minWidth: 15 }}
        className="ag-header-icon ag-sort-ascending-icon"
        aria-hidden="true"
      >
        <span className="ag-icon ag-icon-asc" unselectable="on" />
      </span>
    )}
    {sort && sort === 'desc' && (
      <span
        style={{ marginLeft: 4, minWidth: 15 }}
        className="ag-header-icon ag-sort-descending-icon"
        aria-hidden="true"
      >
        <span className="ag-icon ag-icon-desc" unselectable="on" />
      </span>
    )}
  </>
);

export default DeltaHeaderCell;
